import React, { useState, useEffect } from 'react';
import { useFamily } from '../context/FamilyContext';
import { subscribeToDocuments } from '@coparent/shared/src/services/documentService';
import { DOCUMENT_CATEGORIES } from '@coparent/shared/src/utils/constants';
import DocumentCard from '../components/documents/DocumentCard';
import DocumentUpload from '../components/documents/DocumentUpload';
import DocumentPreview from '../components/documents/DocumentPreview';
import { HiPlus, HiSearch, HiDocumentText } from 'react-icons/hi';
import NotificationBell from '../components/notifications/NotificationBell';

const DocumentsPage = () => {
  const { familyId } = useFamily();
  const [documents, setDocuments] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!familyId) return;
    const unsub = subscribeToDocuments(familyId, setDocuments);
    return () => unsub();
  }, [familyId]);

  const filteredDocuments = documents.filter(doc => {
    if (categoryFilter !== 'all' && doc.category !== categoryFilter) return false;
    if (searchQuery && !doc.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between page-header">
        <div>
          <h1 className="page-title">Documents</h1>
          <p className="page-subtitle">Shared files and records</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowUpload(true)}
            className="btn-primary flex items-center gap-2 text-sm"
          >
            <HiPlus className="text-base" />
            Upload
          </button>
          <NotificationBell />
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <HiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-300" />
        <input
          type="text"
          placeholder="Search documents..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input-base pl-10"
        />
      </div>

      {/* Category filters */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <button
          onClick={() => setCategoryFilter('all')}
          className={categoryFilter === 'all' ? 'chip-active' : 'chip-inactive'}
        >
          All
        </button>
        {DOCUMENT_CATEGORIES.map(cat => (
          <button
            key={cat.value}
            onClick={() => setCategoryFilter(cat.value)}
            className={categoryFilter === cat.value ? 'chip-active' : 'chip-inactive'}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Document grid */}
      {filteredDocuments.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDocuments.map(doc => (
            <DocumentCard
              key={doc.id}
              document={doc}
              onPreview={setPreviewDoc}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 text-ink-300">
          <HiDocumentText className="text-4xl mx-auto mb-3 text-ink-200" />
          <p className="text-base font-semibold">
            {documents.length === 0 ? 'No documents yet' : 'No documents match your filters'}
          </p>
          <p className="text-sm mt-1">
            {documents.length === 0
              ? 'Upload your first document to get started.'
              : 'Try adjusting your search or category filter.'}
          </p>
        </div>
      )}

      {showUpload && <DocumentUpload onClose={() => setShowUpload(false)} />}
      {previewDoc && <DocumentPreview document={previewDoc} onClose={() => setPreviewDoc(null)} />}
    </div>
  );
};

export default DocumentsPage;
